import React from "react";
import PropTypes from "prop-types";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Copy01, Edit01, Trash01 } from "@opendate-app/setlist";
import { TicketTypeRowView, TicketTypeTableHeader, formatPrice } from "../../ConfirmTicketing/components/TicketTypeRowView";
import { DAY_OF_SHOW_NAME, inventoryLabel, onSaleLabel } from "../scheduleBuilder";
import { offsetParts } from "./ShowDetailOffset";

const atLabel = (name, minutes) => {
  if (!Number(minutes)) return name;
  const { amount, unit, direction } = offsetParts(minutes);
  return `${amount} ${amount === "1" ? unit.replace(/s$/, "") : unit} ${direction} ${name}`;
};

// The same windows a confirm row expands into, told relative to each date's
// show details because a series has no dates of its own yet.
export const seriesSalesWindows = (type) => {
  const price = type.price;
  const presales = (type.presaleWindows || []).map((window) => ({
    key: `presale-${window.id}`,
    kind: "presale",
    name: window.name,
    price: window.price === "" || window.price == null ? price : window.price,
    sub: `${window.accessCode ? `Code ${window.accessCode} · ` : ""}until ${atLabel(window.endActivity, window.endOffsetMinutes)}`,
    status: `Opens ${atLabel(window.startActivity, window.startOffsetMinutes)}`,
  }));
  const priceChanges = (type.pricePoints || [])
    .filter((point) => point.showActivityName)
    .map((point) => ({
      key: `price-point-${point.id}`,
      kind: "presale",
      name: point.name || "Price change",
      price: point.price,
      sub: "Scheduled price",
      status: `From ${atLabel(point.showActivityName, point.offsetMinutes)}`,
    }));
  const dayOf = (type.pricePoints || []).find((point) => !point.showActivityName);
  return [
    ...presales,
    ...priceChanges,
    { key: "public", kind: "public", name: "Public on-sale", price, sub: "Everyone", status: onSaleLabel(type) },
    ...(dayOf ? [{ key: "day-of", kind: "dayOf", name: DAY_OF_SHOW_NAME, price: dayOf.price, sub: "Until event end", status: "From doors on each date" }] : []),
  ];
};

const priceDisplayFor = (type, windows) => {
  if (!Number(type.price)) return type.sectionKey && !type.free ? "Set a price" : "FREE";
  const prices = windows.map((window) => Number(window.price)).filter((amount) => !Number.isNaN(amount));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min) : `${formatPrice(min)} – ${formatPrice(max)}`;
};

const SeriesTicketRow = ({ type, schedule, last, actions, onEdit }) => {
  const seated = Boolean(type.sectionKey);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: type.id, disabled: seated });
  const windows = seriesSalesWindows(type);
  const free = !Number(type.price) && !(seated && !type.free);
  return (
    <TicketTypeRowView
      sortableRef={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      dragHandleProps={{ ...attributes, ...listeners }}
      draggable={!seated}
      last={last}
      name={type.name || (seated ? "Untitled section" : "Untitled ticket")}
      subLine={free ? "Comp / free" : `${windows.length} sales window${windows.length === 1 ? "" : "s"}${windows.length > 1 ? " · 1 shared pool" : ""}`}
      salesWindowText={onSaleLabel(type)}
      priceDisplay={priceDisplayFor(type, windows)}
      inventory={<span className="ticket-type-row-v2-inventory">{inventoryLabel(type, schedule)}</span>}
      actions={actions}
      windows={windows}
      free={free}
      basePrice={type.price}
      poolCount={inventoryLabel(type, schedule)}
      soldPercent={0}
      poolStats={null}
      onEditWindow={onEdit}
    />
  );
};

// The confirm's redesign ticket table, fed by series rows: drag to reorder
// (the order becomes each date's positions), Edit, Copy and Delete.
const SeriesTicketList = ({ ticketTypes, schedule, onEdit, onCopy, onDelete, onMove, empty }) => {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  if (ticketTypes.length === 0) return <p className="series-edit-hint">{empty}</p>;

  const actionsFor = (type) => [
    { label: "Edit", icon: Edit01, onClick: () => onEdit(type) },
    ...(type.sectionKey ? [] : [
      { label: "Copy", icon: Copy01, onClick: () => onCopy(type) },
      { label: "Delete", icon: Trash01, destructive: true, onClick: () => onDelete(type) },
    ]),
  ];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        onMove(ticketTypes.findIndex((type) => type.id === active.id), ticketTypes.findIndex((type) => type.id === over.id));
      }}
    >
      <div className="ticket-type-v2-table" data-testid="series-ticket-list">
        <TicketTypeTableHeader />
        <SortableContext items={ticketTypes.map((type) => type.id)} strategy={verticalListSortingStrategy}>
          <div className="ticket-type-v2-body">
            {ticketTypes.map((type, index) => (
              <SeriesTicketRow
                key={type.id}
                type={type}
                schedule={schedule}
                last={index === ticketTypes.length - 1}
                actions={actionsFor(type)}
                onEdit={() => onEdit(type)}
              />
            ))}
          </div>
        </SortableContext>
      </div>
    </DndContext>
  );
};

SeriesTicketList.propTypes = {
  ticketTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  schedule: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  empty: PropTypes.string,
};

export default SeriesTicketList;
